/**
 * The quantity the "Add to order" stepper shows, when the cart changes underneath it.
 *
 * The control keeps the typed quantity in its own state, seeded from the cart ONCE at mount, and
 * decides whether to offer the button by comparing the two (`typed === cartQty` means there is
 * nothing to send). So its state and the cart are two answers to one question, and nothing kept
 * them together: any change to the cart from somewhere else — the bulk grid, Reorder, the order
 * screen, or this row's own write landing — left the stepper showing a figure the cart had moved
 * on from. Pressing the button then sent the stale figure, which for a row showing 0 means
 * DELETING the line the cart had just gained.
 *
 * `lastSeen` is what the cart said when the stepper was last in step with it, which is what
 * tells "the user has typed something since" from "the user has not touched this".
 */
export interface StepperState {
  /** The quantity in the control now. */
  typed: number;
  /** What the cart said the last time the two agreed. */
  lastSeen: number;
  /** What the cart says now. */
  cartQty: number;
}

/**
 * The quantity to show after the cart moved.
 *
 * An untouched control follows the cart, so the row tells the truth about the order. A control
 * the retailer has typed into keeps what they typed: they are mid-edit, and the cart changing
 * elsewhere is no reason to discard a number somebody chose.
 */
export function syncedQty({ typed, lastSeen, cartQty }: StepperState): number {
  if (typed === lastSeen) return cartQty;
  return typed;
}
